import { useState } from "react";
import { Button } from "../../components/button/button";
import EmailList from "../../components/email-list/email-list";
import { Tag } from "../../components/tag/tag";
import UserService from "../../utilities/services/user-service";
import MskScoreService from "../../utilities/services/msk-score-service";
import { scheduleAiMskScoreSummary } from "../../utilities/ai-response-utils";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { normalizeEmail } from "../../utilities/email-util";

const COMPONENT_CLASS = "c-partial-msk-summary-tool";

const RegeneratePartialMskSummaryTool: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<{ email: string; status: string; aiResponseId?: string }[]>([]);
    const [pendingEmails, setPendingEmails] = useState<string[]>([]);

    const handleSubmit = async () => {
        if (pendingEmails.length === 0) return;

        setLoading(true);
        setResults([]);

        const newResults: { email: string; status: string; aiResponseId?: string }[] = [];

        for (const email of pendingEmails) {
            try {
                const users = await UserService.getBy([
                    { field: "email", operator: "==", value: normalizeEmail(email) },
                ]);

                if (!users || users.length === 0) {
                    newResults.push({ email, status: "No user found" });
                    continue;
                }

                const user = users[0];
                if (!user.id) {
                    newResults.push({ email, status: "User has no ID" });
                    continue;
                }

                const mskScores = await MskScoreService.getBy(
                    [{ field: "userId", operator: "==", value: user.id }],
                    [{ field: "created", direction: "desc" }],
                    1
                );

                const mskScore = mskScores?.[0] as MskScore | undefined;
                if (!mskScore) {
                    newResults.push({ email, status: "No viable MSK score" });
                    continue;
                }

                const aiResponse = await scheduleAiMskScoreSummary(mskScore, user.organizationId);

                newResults.push({ email, status: "Success", aiResponseId: aiResponse.id });
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error";
                newResults.push({ email, status: `Error: ${errorMessage}` });
            }
        }

        setResults(newResults);
        setLoading(false);
    };

    return (
        <div className={COMPONENT_CLASS}>
            <h2>Regenerate Partial MSK Summary</h2>
            <p>
                Select users to regenerate AI partial MSK summaries. Each user with a viable MSK score
                will have a new aiResponse generated.
            </p>

            <div className={`${COMPONENT_CLASS}__input`}>
                <label>Select Users</label>
                <EmailList
                    enableSuggestions
                    organizationId={null}
                    onEmailsAdded={(emails) => {
                        setPendingEmails((prev) => [...new Set([...prev, ...emails])]);
                    }}
                    onEmailsError={() => {}}
                />
            </div>

            {pendingEmails.length > 0 && (
                <div className="mb-3">
                    {pendingEmails.map((email, i) => (
                        <Tag
                            id={i.toString()}
                            key={email}
                            onCloseClick={(index) => {
                                setPendingEmails((prev) => prev.filter((_, idx) => idx !== parseInt(index)));
                            }}
                        >
                            {email}
                        </Tag>
                    ))}
                </div>
            )}

            <div className={`${COMPONENT_CLASS}__actions`}>
                <Button
                    onClick={handleSubmit}
                    buttonText={loading ? "Loading..." : "Regenerate Summaries"}
                    disabled={loading || pendingEmails.length === 0}
                />
            </div>

            {results.length > 0 && (
                <div className={`${COMPONENT_CLASS}__content`}>
                    <label>Results</label>
                    <table className="light" cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>aiResponse ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((r, i) => (
                                <tr key={i}>
                                    <td>{r.email}</td>
                                    <td>{r.status}</td>
                                    <td>{r.aiResponseId || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RegeneratePartialMskSummaryTool;
